import React from 'react';
import {
  KAMISHIBAI_NA_CELL_BG,
  KAMISHIBAI_NA_STYLE,
  KAMISHIBAI_SYMBOLS,
  KAMISHIBAI_SYMBOL_STYLES,
  lstStatusToDisplaySymbol,
  type KamishibaiSymbol,
} from '../../utils/kamishibaiStatus';
import { ProcessStoppedBadge } from './ProcessStoppedControl';
import type { LstStatus } from '../../types';

interface KamishibaiSymbolProps {
  symbol: KamishibaiSymbol;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  title?: string;
}

const SIZE: Record<NonNullable<KamishibaiSymbolProps['size']>, string> = {
  sm: 'h-6 w-6 text-[11px]',
  md: 'h-8 w-8 text-[13px]',
  lg: 'h-10 w-10 text-[15px]',
};

/** Célula ou badge com símbolo O / △ / X e cor semântica. */
export const KamishibaiSymbolBadge: React.FC<KamishibaiSymbolProps> = ({
  symbol,
  size = 'md',
  className = '',
  title,
}) => {
  const meta = KAMISHIBAI_SYMBOLS[symbol];
  const styles = KAMISHIBAI_SYMBOL_STYLES[symbol];

  return (
    <span
      title={title ?? meta.label}
      className={`inline-flex items-center justify-center rounded-md font-bold tabular-nums ring-1 ${styles.bg} ${styles.text} ${styles.ring} ${SIZE[size]} ${className}`}
      aria-label={meta.label}
    >
      {symbol === 'triangle' ? (
        <span className="leading-none translate-y-[-1px]">{meta.glyph}</span>
      ) : (
        meta.glyph
      )}
    </span>
  );
};

interface KamishibaiActionButtonProps {
  symbol: KamishibaiSymbol;
  active?: boolean;
  disabled?: boolean;
  /** Rótulo exibido abaixo do símbolo (padrão: meta.label) */
  label?: string;
  onClick: () => void;
}

/** Botão de decisão Kamishibai — usado no rodapé da sidebar contextual. */
export const KamishibaiActionButton: React.FC<KamishibaiActionButtonProps> = ({
  symbol,
  active = false,
  disabled = false,
  label,
  onClick,
}) => {
  const meta = KAMISHIBAI_SYMBOLS[symbol];
  const styles = KAMISHIBAI_SYMBOL_STYLES[symbol];
  const displayLabel = label ?? meta.label;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      aria-label={displayLabel}
      className={`flex min-h-[72px] flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 px-2 py-3 transition-all active:scale-[0.98] disabled:opacity-50 ${
        active ? styles.buttonActive : styles.button
      }`}
    >
      <span className="text-[28px] font-bold leading-none">
        {symbol === 'triangle' ? meta.glyph : meta.glyph}
      </span>
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide ${active ? 'text-white/90' : 'text-slate-500 dark:text-slate-400'}`}
      >
        {displayLabel}
      </span>
    </button>
  );
};

interface KamishibaiMatrixCellProps {
  status?: LstStatus | 'pendente' | 'na' | null;
  symbol?: KamishibaiSymbol | 'na' | null;
  onClick?: () => void;
  isToday?: boolean;
  isWeekend?: boolean;
  label?: string;
}

/** Célula da matriz — O / Δ / X / Parado. N/A = fundo cinza, sem símbolo. */
export const KamishibaiMatrixCell: React.FC<KamishibaiMatrixCellProps> = ({
  status,
  symbol: symbolProp,
  onClick,
  isToday = false,
  isWeekend = false,
  label,
}) => {
  const display =
    symbolProp !== undefined
      ? symbolProp
      : status === 'na'
        ? 'na'
        : status && status !== 'pendente'
          ? lstStatusToDisplaySymbol(status)
          : status === 'pendente'
            ? 'pending'
            : null;

  const isNa = display === 'na';
  const bg = isNa
    ? KAMISHIBAI_NA_CELL_BG
    : isToday
      ? 'bg-blue-50/60 dark:bg-blue-950/20'
      : isWeekend
        ? 'bg-slate-50 dark:bg-slate-800/40'
        : 'bg-white dark:bg-slate-950';

  let content: React.ReactNode = null;
  if (display === 'stopped') {
    content = <ProcessStoppedBadge size="sm" />;
  } else if (display === 'O' || display === 'triangle' || display === 'X') {
    content = <KamishibaiSymbolBadge symbol={display} size="sm" />;
  } else if (display === 'pending') {
    content = (
      <span
        className={`text-[9px] font-semibold ${KAMISHIBAI_NA_STYLE.text}`}
        title="Planejado"
        aria-label="Planejado"
      >
        ·
      </span>
    );
  } else if (display !== 'na' && display !== null) {
    content = (
      <span
        className="text-[10px] font-bold text-slate-300 dark:text-slate-600"
        title="Planejado"
        aria-label="Planejado"
      >
        ·
      </span>
    );
  }

  if (!onClick) {
    return (
      <div className={`flex h-8 w-9 items-center justify-center ${bg}`}>{content}</div>
    );
  }

  if (isNa) {
    return (
      <div
        className={`flex h-8 w-9 items-center justify-center ${bg}`}
        aria-hidden
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`flex h-8 w-9 items-center justify-center outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-blue-400 ${bg}`}
    >
      {content}
    </button>
  );
};
